/**
 * Video Recording Configuration
 *
 * Centralized constants for video recording features
 * Keeps magic numbers out of components
 */

/**
 * Frame extraction intervals (seconds)
 */
export const FRAME_INTERVALS = {
  MIN: 1,
  DEFAULT: 3,
  MAX: 60,
  RECOMMENDED: 3, // Best balance of cost vs coverage
};

/**
 * Video session durations (seconds)
 */
export const DURATIONS = {
  MIN: 10,
  DEFAULT: 300, // 5 minutes
  MAX: 1800, // 30 minutes
};

/**
 * UI constants
 */
export const UI = {
  COUNTER_POSITION: { top: 16, left: 16 },
  EXIT_BUTTON_POSITION: { bottom: 24, left: 24 },
  EXIT_BUTTON_SIZE: 64,
  SPINNER_SIZE: 24,
  COUNTER_OPACITY: 0.7,
  SPINNER_OPACITY: 0.6,
};

/**
 * Session ID prefixes for different modes
 */
export const SESSION_PREFIXES = {
  STEALTH_VIDEO: 'stealth_video',
  VIDEO_RECORDER: 'video_recorder',
};

/**
 * Capture type identifiers
 */
export const CAPTURE_TYPES = {
  STEALTH_VIDEO: 'stealth_video',
  VIDEO_FRAME: 'video_frame',
  STILL_IMAGE: 'camera',
};

/**
 * Cost estimation constants
 */
export const COSTS = {
  PER_FRAME_DETECT_AND_SEARCH: 0.002, // DetectFaces + SearchFacesByImage
  PER_MINUTE_VIDEO_API: 0.10, // Rekognition Video API
};

/**
 * Calculate estimated cost for frame extraction
 * @param {number} durationSeconds - Video duration in seconds
 * @param {number} intervalSeconds - Frame extraction interval
 * @returns {Object} Cost breakdown
 */
export function calculateFrameExtractionCost(durationSeconds, intervalSeconds) {
  const frameCount = Math.floor(durationSeconds / intervalSeconds);
  const frameCost = frameCount * COSTS.PER_FRAME_DETECT_AND_SEARCH;
  const videoCost = (durationSeconds / 60) * COSTS.PER_MINUTE_VIDEO_API;
  const savings = videoCost - frameCost;
  const savingsPercent = (savings / videoCost) * 100;

  return {
    frameCount,
    frameCost,
    videoCost,
    savings,
    savingsPercent,
    recommendation: frameCost < videoCost ? 'frame_extraction' : 'video_api'
  };
}

/**
 * Validate frame interval
 * @param {number} interval - Proposed interval in seconds
 * @returns {number} Valid interval (clamped to min/max)
 */
export function validateFrameInterval(interval) {
  return Math.max(FRAME_INTERVALS.MIN, Math.min(FRAME_INTERVALS.MAX, interval));
}

/**
 * Validate session duration
 * @param {number} duration - Proposed duration in seconds
 * @returns {number} Valid duration (clamped to min/max)
 */
export function validateDuration(duration) {
  return Math.max(DURATIONS.MIN, Math.min(DURATIONS.MAX, duration));
}
